import { ArrowRight, Check, Dumbbell, Mic, Play, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Band, BRAND, ButtonLink, DISH, EX, Logo, PhotoAvatar, SectionIntro } from "./primitives";
import {
  CheckinsVisual,
  InboxVisual,
  MealPlanVisual,
  MemoryVisual,
  NutritionVisual,
  VideosVisual,
  ProposalVisual,
  RemindersVisual,
  TrainingVisual,
} from "./FeatureVisuals";

/* ---------------------------------------------------------------- proof row */

export function ProofRow() {
  const items = [
    {
      lead: (
        <div className="flex">
          <PhotoAvatar src={BRAND.martin} size={36} alt="Martin" />
          <PhotoAvatar src={BRAND.nasim} size={36} alt="Nasim" className="-ml-2.5" />
        </div>
      ),
      title: "Built by coaches",
      text: "Shaped by working coaches like Martin and Nasim. Every feature has to earn its place on the gym floor.",
    },
    {
      lead: <IconTile icon={Mic} />,
      title: "Talk, don't type",
      text: "Tap your assistant and speak in your own words. It uses the same buttons you would, right on the screen.",
    },
    {
      lead: <IconTile icon={Check} />,
      title: "You approve, it sends",
      text: "Nothing reaches a client until you tap approve. Every message is logged on their profile.",
    },
  ];
  return (
    <Band frameClassName="grid md:grid-cols-3">
      {items.map((it, i) => (
        <div
          key={it.title}
          data-reveal
          className={cn("flex flex-col gap-3 px-6 py-8 sm:px-8 sm:py-9", i > 0 && "border-t border-line md:border-l md:border-t-0")}
        >
          <div className="flex items-center gap-3">
            {it.lead}
            <span className="text-[22px] font-semibold tracking-[-0.4px] sm:text-2xl">{it.title}</span>
          </div>
          <span className="text-sm leading-[22px] text-neutral-500">{it.text}</span>
        </div>
      ))}
    </Band>
  );
}

function IconTile({ icon: Icon }) {
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-ink">
      <Icon className="h-[18px] w-[18px] text-lemon" strokeWidth={2.4} />
    </span>
  );
}

/* ---------------------------------------------------------------- feature sections */

function FeatureColumns({ cols }) {
  return (
    <div className="grid border-t border-line md:grid-cols-3">
      {cols.map((c, i) => (
        <div
          key={c.title}
          data-reveal
          className={cn("flex flex-col gap-8 p-5", i > 0 && "border-t border-line md:border-l md:border-t-0")}
        >
          <div className="relative h-[360px] overflow-hidden rounded-xl border border-line bg-soft">
            {c.visual}
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="m-0 text-base font-semibold leading-6">{c.title}</h3>
            <p className="m-0 text-sm leading-[23px] text-neutral-500">{c.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function IntroButtons({ second, href }) {
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <ButtonLink href="/signup">Start for free</ButtonLink>
      <ButtonLink variant="light" href={href}>
        {second}
        <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
      </ButtonLink>
    </div>
  );
}

export function CoachingSection() {
  return (
    <Band id="coaching">
      <SectionIntro
        icon={Dumbbell}
        label="Coaching"
        title="Everything you need to coach"
        cta={<IntroButtons second="Explore coaching" href="#coaching" />}
      >
        Training, nutrition and check-ins in one place, with a video exercise library your clients
        can actually follow.
      </SectionIntro>
      <FeatureColumns
        cols={[
          {
            visual: <TrainingVisual />,
            title: "Training",
            text: "5,900+ exercises in body part folders, each with a men's and women's video demo. Build programs and they land on the client's phone.",
          },
          {
            visual: <NutritionVisual />,
            title: "Nutrition",
            text: "Calorie and macro targets, meal plans with a photo of every dish, and a quick food log for clients.",
          },
          {
            visual: <CheckinsVisual />,
            title: "Check-ins",
            text: "Weekly forms, progress photos and measurements. See who needs you first.",
          },
        ]}
      />
    </Band>
  );
}

export function AgentSection() {
  return (
    <Band id="agent">
      <SectionIntro
        icon={Mic}
        label="AI Assistant"
        title="You talk. It gets it done."
        cta={<IntroButtons second="Meet your assistant" href="#assistant" />}
      >
        Tap your assistant and say what you need. It adds clients, builds workouts and meal plans,
        sends videos, writes proposals and remembers the details, so you don't have to.
      </SectionIntro>
      <FeatureColumns
        cols={[
          {
            visual: <MealPlanVisual />,
            title: "Meal plans, with photos",
            text: "Say the calories and what they can't eat. It writes the whole plan and makes a photo of every dish.",
          },
          {
            visual: <VideosVisual />,
            title: "Sends exercise videos",
            text: "\"Send Daniel his chest videos.\" It picks the right demos and sends them to the client's chat once you approve.",
          },
          {
            visual: <MemoryVisual />,
            title: "Remembers your clients",
            text: "Likes, dislikes, schedules and how you coach. It remembers from every chat, so you never repeat yourself.",
          },
        ]}
      />
      <FeatureColumns
        cols={[
          {
            visual: <ProposalVisual />,
            title: "Proposals",
            text: "Say who the client is and what they need. It drafts the proposal email. You read it and tap approve.",
          },
          {
            visual: <RemindersVisual />,
            title: "Reminders",
            text: "Set reminders by voice and let your assistant chase them. Nobody slips through the cracks again.",
          },
          {
            visual: <InboxVisual />,
            title: "Email tracking",
            text: "See who opened, who replied and who went quiet. It drafts the follow-up for you.",
          },
        ]}
      />
    </Band>
  );
}

/* ---------------------------------------------------------------- meet your assistant */

const PERSONALITIES = [
  ["Funny", "Cracks a joke, then gets it done."],
  ["Friendly", "Warm and encouraging, like a good teammate."],
  ["Direct", "Short answers. No small talk."],
];
const VOICE_NAMES = ["Marin", "Cedar", "Coral", "Ash", "Sage", "Verse"];

export function AssistantSection() {
  return (
    <Band id="assistant">
      <div className="grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="relative flex items-center justify-center overflow-hidden bg-soft px-6 py-12 sm:py-16">
          <div
            className="absolute inset-0 opacity-70"
            style={{ backgroundImage: "radial-gradient(#E5E5E5 1px, transparent 1px)", backgroundSize: "16px 16px" }}
            aria-hidden="true"
          />
          <img
            data-reveal
            src={BRAND.assistant}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/brand/agent/base.webp";
            }}
            alt="Your Fitamingo assistant"
            loading="lazy"
            className="relative w-[min(340px,80%)] drop-shadow-[0_24px_32px_rgba(0,0,0,0.12)]"
          />
        </div>
        <div className="flex flex-col border-t border-line md:border-l md:border-t-0">
          <SectionIntro icon={Mic} label="Meet your assistant" title="Make it yours">
            Pick its voice and its personality in Settings. It lives in the corner of your screen: tap it
            to talk, and it shows each step as it works.
          </SectionIntro>
          <div data-reveal className="grid gap-2 px-4 pb-6 sm:grid-cols-3 sm:px-10">
            {PERSONALITIES.map(([t, d], i) => (
              <div key={t} className={cn("rounded-xl border p-3.5", i === 0 ? "border-ink bg-ink text-white" : "border-line bg-white")}>
                <span className="text-[15px] font-semibold">{t}</span>
                <p className={cn("m-0 mt-1 text-[13px] leading-5", i === 0 ? "text-neutral-300" : "text-neutral-500")}>{d}</p>
              </div>
            ))}
          </div>
          <div data-reveal className="flex flex-wrap gap-2 px-4 pb-10 sm:px-10">
            {VOICE_NAMES.map((v, i) => (
              <span
                key={v}
                className={cn(
                  "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium",
                  i === 0 ? "border-lemon bg-lemon text-black" : "border-line bg-white text-neutral-600",
                )}
              >
                <Play className={cn("h-3 w-3", i === 0 ? "fill-black" : "fill-neutral-400 text-neutral-400")} />
                {v}
              </span>
            ))}
          </div>
          <ul className="mt-auto grid border-t border-line sm:grid-cols-3">
            {[
              ["Shows its work", "Thinking, cooking, putting it together. Then a tick for every step."],
              ["Asks before sending", "Nothing reaches a client until you tap approve."],
              ["Hangs up by itself", "After 45 quiet seconds, so it never runs up your bill."],
            ].map(([t, d], i) => (
              <li key={t} data-reveal className={cn("flex flex-col gap-1.5 px-4 py-6 sm:px-6", i > 0 && "border-t border-line sm:border-l sm:border-t-0")}>
                <span className="flex items-center gap-2 text-[14.5px] font-semibold">
                  <Check className="h-4 w-4 text-ink" strokeWidth={2.6} />
                  {t}
                </span>
                <span className="text-[13.5px] leading-[21px] text-neutral-500">{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Band>
  );
}

/* ---------------------------------------------------------------- trial strip */

export function TrialStrip() {
  return (
    <Band id="pricing">
      <div
        data-reveal
        className="flex flex-wrap items-center gap-8 px-6 py-14 sm:px-[60px] sm:py-[72px]"
        style={{ backgroundImage: "radial-gradient(#E5E5E5 1px, transparent 1px)", backgroundSize: "16px 16px" }}
      >
        <h2 className="m-0 flex-[1_1_420px] text-[30px] font-normal leading-[1.22] tracking-[-0.6px] sm:text-[40px] sm:leading-[49px]">
          Start with a 14-day free trial.
          <br />
          <span className="text-neutral-400">No card needed to start.</span>
        </h2>
        <div className="flex gap-3">
          <ButtonLink href="/signup">Start for free</ButtonLink>
          <ButtonLink variant="light" href="/pricing">
            See pricing
          </ButtonLink>
        </div>
      </div>
    </Band>
  );
}

/* ---------------------------------------------------------------- coaches */

function CoachCard({ src, name, text, className }) {
  return (
    <div data-reveal className={cn("flex flex-col gap-5 p-5", className)}>
      <div className="relative h-[380px] overflow-hidden rounded-xl bg-soft sm:h-[460px]">
        <img src={src} alt={`${name}, coach`} loading="lazy" className="h-full w-full object-cover object-[50%_18%]" />
        <span className="absolute left-3.5 top-3.5 flex h-[26px] items-center gap-1.5 rounded-lg bg-white px-2.5 text-[12.5px] font-medium">
          <span className="h-[7px] w-[7px] rounded-full bg-green-500" />
          Coaching on Fitamingo
        </span>
      </div>
      <p className="m-0 text-lg font-medium leading-7 tracking-[-0.2px]">{text}</p>
      <div className="flex items-center gap-2.5">
        <PhotoAvatar src={src} size={32} />
        <div className="flex flex-col">
          <span className="text-sm font-semibold">{name}</span>
          <span className="text-[13px] text-neutral-500">Coach</span>
        </div>
      </div>
    </div>
  );
}

export function CoachesSection() {
  return (
    <Band id="coaches">
      <SectionIntro icon={Users} label="Coaches" title="Made on the gym floor" center>
        Built around how real coaches work: more time with clients, less time on admin.
      </SectionIntro>
      <div className="grid border-t border-line md:grid-cols-2">
        <CoachCard src={BRAND.martin} name="Martin" text="Strength and muscle gain. Programs built from the Fitamingo workout library." />
        <CoachCard
          src={BRAND.nasim}
          name="Nasim"
          text="Nutrition and fat loss. Meal plans, macro targets and weekly check-ins."
          className="border-t border-line md:border-l md:border-t-0"
        />
      </div>
    </Band>
  );
}

/* ---------------------------------------------------------------- dark CTA */

export function ClosingCta() {
  return (
    <Band>
      <div
        data-reveal
        className="flex flex-col items-center bg-ink px-6 py-24 text-center text-neutral-50 sm:px-10 sm:py-[120px]"
      >
        <h2 className="max-w-[620px] text-balance text-[34px] font-medium leading-[1.1] tracking-[-0.96px] sm:text-5xl sm:leading-[52px]">
          Hand the admin to Fitamingo
        </h2>
        <p className="mt-4 max-w-[520px] text-base leading-7 text-neutral-400 sm:text-lg">
          Spend your time coaching. Let your assistant build, send and remember the rest.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <ButtonLink variant="inverse" href="/signup">
            Start for free
          </ButtonLink>
          <ButtonLink variant="ghostDark" href="#assistant">
            <Mic className="h-[15px] w-[15px]" strokeWidth={2} />
            Meet your assistant
          </ButtonLink>
        </div>
      </div>
    </Band>
  );
}

/* ---------------------------------------------------------------- footer */

const FOOTER = [
  ["Platform", ["Clients", "Workouts", "Nutrition", "Check-ins", "Payments"]],
  ["AI Assistant", ["Meal plans with photos", "Exercise videos", "Memory", "Proposals", "Reminders", "Email tracking"]],
  ["Resources", ["Help centre", "Exercise library", "Blog"]],
  ["Company", ["About", "Pricing", "Contact", "Privacy"]],
];

export function SiteFooter() {
  return (
    <footer className="border-t border-line bg-soft">
      <div className="mx-auto flex max-w-frame flex-wrap gap-12 border-x border-guide px-6 pb-10 pt-14 sm:px-10">
        <div className="flex flex-[1_1_260px] flex-col gap-3.5">
          <Logo />
          <span className="max-w-[280px] text-sm leading-[22px] text-neutral-500">
            The coaching platform with an AI assistant that does the admin.
          </span>
        </div>
        {FOOTER.map(([title, links]) => (
          <div key={title} className="flex min-w-[130px] flex-col gap-2.5">
            <h3 className="m-0 mb-1 text-sm font-semibold">{title}</h3>
            {links.map((l) => (
              <a key={l} href="#top" className="text-sm text-neutral-500 hover:text-ink">
                {l}
              </a>
            ))}
          </div>
        ))}
        <div className="flex basis-full justify-between border-t border-line pt-6 text-[13px] text-neutral-400">
          <span>© {new Date().getFullYear()} Fitamingo</span>
          <span>Made for coaches</span>
        </div>
      </div>
    </footer>
  );
}


/* ---------------------------------------------------------------- for clients */

function PhoneMock() {
  return (
    <div className="relative mx-auto w-[260px] rounded-[38px] border-[7px] border-[#0A0A0A] bg-[#0A0A0A] shadow-[0_30px_80px_-24px_rgba(0,0,0,0.45)]" aria-hidden="true">
      <div className="overflow-hidden rounded-[31px] bg-white font-app">
        <div className="flex items-center justify-between px-4 pb-2 pt-3">
          <span className="text-[10px] font-semibold">9:41</span>
          <span className="h-[18px] w-[72px] rounded-full bg-[#0A0A0A]" />
          <span className="text-[10px] font-semibold">100%</span>
        </div>
        <div className="flex flex-col gap-2.5 px-3.5 pb-4">
          <div>
            <p className="text-[11px] text-neutral-500">Tuesday</p>
            <p className="text-[17px] font-semibold leading-tight tracking-[-0.2px]">Morning, Sarah</p>
          </div>
          <div className="overflow-hidden rounded-2xl bg-[#0A0A0A] text-white">
            <div className="relative flex h-[96px] items-center justify-center bg-white">
              <img src={EX.gobletF.src} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-lemon shadow">
                <span className="ml-0.5 h-0 w-0 border-y-[6px] border-l-[9px] border-y-transparent border-l-black" />
              </span>
              <span className="absolute bottom-1.5 left-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[9px]">Goblet squat · demo</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <div>
                <p className="text-[12px] font-semibold">Lower body A</p>
                <p className="text-[10px] text-neutral-400">6 exercises · 45 min</p>
              </div>
              <span className="rounded-full bg-lemon px-2.5 py-1 text-[10px] font-semibold text-black">Start</span>
            </div>
          </div>
          <div className="rounded-2xl border border-line p-2.5">
            <p className="mb-1.5 text-[11px] font-semibold">Today's habits</p>
            {[
              ["10k steps", true],
              ["3 L water", true],
              ["8 h sleep", false],
            ].map(([h, done]) => (
              <div key={h} className="flex items-center gap-2 py-0.5 text-[11px]">
                <span className={cn("flex h-3.5 w-3.5 items-center justify-center rounded", done ? "bg-ink" : "border border-neutral-300")}>
                  {done && <Check className="h-2.5 w-2.5 text-lemon" strokeWidth={3} />}
                </span>
                <span className={done ? "text-neutral-400 line-through" : ""}>{h}</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-line p-2.5">
            <div className="mb-1 flex justify-between text-[11px]">
              <span className="font-semibold">Food</span>
              <span className="text-neutral-500">1,240 / 1,900 kcal</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-neutral-100">
              <span className="block h-full w-[65%] rounded-full bg-ink" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <img src={DISH.bowl.src} alt="" className="h-8 w-8 rounded-lg object-cover" />
              <div className="min-w-0">
                <p className="truncate text-[10.5px] font-medium">Lunch: chicken quinoa bowl</p>
                <p className="text-[9.5px] text-neutral-500">620 kcal</p>
              </div>
            </div>
          </div>
          <div className="self-start rounded-2xl rounded-bl-md bg-neutral-100 px-3 py-2 text-[11px] leading-4">
            Great squats yesterday! Add 2.5 kg today. 💪
            <span className="mt-0.5 block text-[9px] text-neutral-400">Coach Martin</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ClientsSection() {
  const items = [
    ["Workouts with video demos", "Every exercise has a men's and women's demo. Log sets and reps as you go."],
    ["Your meal plan", "Meals with photos, portions and macros from your coach, plus a quick food log."],
    ["Weekly check-ins", "Weight, photos and how the week went, in two minutes."],
    ["Chat with your coach", "Questions, form videos and feedback in one place."],
  ];
  return (
    <Band id="clients">
      <div className="grid md:grid-cols-2">
        <div className="flex flex-col">
          <SectionIntro icon={Users} label="For clients" title="Training with a Fitamingo coach?">
            Your coach set up your own training app. Your plan, your meals and your progress, all on your phone.
          </SectionIntro>
          <div data-reveal className="-mt-6 flex flex-wrap gap-3 px-4 sm:px-10">
            <ButtonLink href="/client">
              Client login
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </ButtonLink>
          </div>
          <ul className="mt-10 grid border-t border-line sm:grid-cols-2">
            {items.map(([t, d], i) => (
              <li key={t} data-reveal className={cn("flex flex-col gap-1.5 px-4 py-6 sm:px-10", i > 0 && "border-t border-line", i === 1 && "sm:border-l sm:border-t-0", i === 3 && "sm:border-l")}>
                <span className="flex items-center gap-2 text-[15px] font-semibold">
                  <Check className="h-4 w-4 text-ink" strokeWidth={2.6} />
                  {t}
                </span>
                <span className="text-sm leading-[22px] text-neutral-500">{d}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col items-center justify-center gap-8 border-t border-line bg-soft px-4 py-14 md:border-l md:border-t-0">
          <div data-reveal>
            <PhoneMock />
          </div>
          <ol data-reveal className="flex w-full max-w-[360px] flex-col gap-3 text-sm">
            {["Your coach sends you an invite link", "Tap it and create your password", "Log in any time at fitamingo.com/client"].map((s, i) => (
              <li key={s} className="flex items-center gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink text-[12px] font-semibold text-lemon">{i + 1}</span>
                <span className="text-neutral-700">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Band>
  );
}
